import Control, { Options as ControlOptions } from 'ol/control/Control';
import TranslationHelper from '../../../../../helpers/TranslationHelper';

export interface IObjectTypeOption {
    id: string;
    title: string;
}

export interface IObjectTypeSwitcherOptions extends ControlOptions {
    title?: string;
    options?: IObjectTypeOption[];
    selected?: string[];
    onSelectionChange?: (selected: string[]) => void;
}

export const DEFAULT_OBJECT_TYPE_OPTIONS: IObjectTypeOption[] = [
    { id: 'tanks', title: 'Tanks' },
    { id: 'vehicles', title: 'Vehicles' },
    { id: 'employees', title: 'Employees' },
];

export class ObjectTypeSwitcher {
    public element: HTMLElement;
    private control: Control;
    private options: IObjectTypeOption[];
    private selected: string[];
    private readonly onSelectionChange?: (selected: string[]) => void;
    private readonly buttonElement: HTMLButtonElement;
    private readonly panelElement: HTMLUListElement;
    private itemCheckboxes: Map<string, HTMLInputElement> = new Map();
    private itemElements: Map<string, HTMLLIElement> = new Map();

    constructor(optOptions: IObjectTypeSwitcherOptions = {}) {
        const element = document.createElement('div');
        element.className =
            'ol-control ol-bar ol-collapsed ol-unselectable ol-layerswitcher object-type-switcher';

        this.element = element;
        this.control = new Control({
            element,
            target: optOptions.target,
        });

        this.options = optOptions.options || DEFAULT_OBJECT_TYPE_OPTIONS;
        this.selected =
            optOptions.selected && optOptions.selected.length > 0
                ? [...optOptions.selected]
                : ['tanks'];
        this.onSelectionChange = optOptions.onSelectionChange;

        const title =
            optOptions.title ||
            TranslationHelper.translate('Monitored objects');
        this.element.title = title;

        this.buttonElement = document.createElement('button');
        this.buttonElement.type = 'button';
        this.buttonElement.title = title;
        this.element.appendChild(this.buttonElement);

        this.buttonElement.addEventListener('click', (e) => {
            e.stopPropagation();
            this.toggle();
        });

        const panelContainer = document.createElement('div');
        panelContainer.className = 'panel-container';

        this.panelElement = document.createElement('ul');
        this.panelElement.className = 'panel';
        panelContainer.appendChild(this.panelElement);
        this.element.appendChild(panelContainer);

        this.renderItems();
    }

    public setMap(map: any) {
        this.control.setMap(map);
    }

    public getMap() {
        return this.control.getMap();
    }

    public setTarget(target: HTMLElement | string) {
        this.control.setTarget(target);
    }

    private renderItems() {
        this.panelElement.innerHTML = '';
        this.itemCheckboxes.clear();
        this.itemElements.clear();

        this.options.forEach((opt) => {
            const isChecked = this.selected.includes(opt.id);

            const li = document.createElement('li');
            if (isChecked) {
                li.classList.add('ol-visible', 'visible');
            }

            const content = document.createElement('div');
            content.className = 'li-content';

            const checkbox = document.createElement('input');
            checkbox.type = 'checkbox';
            checkbox.id = `object-type-${opt.id}`;
            checkbox.className = 'ol-visibility';
            checkbox.checked = isChecked;

            const label = document.createElement('label');
            label.htmlFor = `object-type-${opt.id}`;
            const span = document.createElement('span');
            span.textContent = TranslationHelper.translate(opt.title);
            label.appendChild(span);

            const handleToggle = (e: Event) => {
                e.stopPropagation();
                e.preventDefault();
                this.toggleOption(opt.id);
            };

            li.addEventListener('click', handleToggle);

            content.appendChild(checkbox);
            content.appendChild(label);
            li.appendChild(content);

            this.panelElement.appendChild(li);
            this.itemCheckboxes.set(opt.id, checkbox);
            this.itemElements.set(opt.id, li);
        });
    }

    public toggleOption(id: string) {
        let newSelected: string[];
        if (this.selected.includes(id)) {
            newSelected = this.selected.filter((item) => item !== id);
        } else {
            newSelected = [...this.selected, id];
        }
        if (newSelected.length === 0) {
            newSelected = ['tanks'];
        }
        this.setSelected(newSelected);
        if (this.onSelectionChange) {
            this.onSelectionChange(this.selected);
        }
    }

    public setSelected(selected: string | string[] | null) {
        if (!selected) {
            this.selected = ['tanks'];
        } else if (Array.isArray(selected)) {
            this.selected = selected.length > 0 ? [...selected] : ['tanks'];
        } else {
            this.selected = [selected];
        }

        this.options.forEach((opt) => {
            const isChecked = this.selected.includes(opt.id);
            const checkbox = this.itemCheckboxes.get(opt.id);
            const li = this.itemElements.get(opt.id);

            if (checkbox) {
                checkbox.checked = isChecked;
            }
            if (li) {
                if (isChecked) {
                    li.classList.add('ol-visible', 'visible');
                } else {
                    li.classList.remove('ol-visible', 'visible');
                }
            }
        });
    }

    public getSelected(): string[] {
        return this.selected;
    }

    public toggle() {
        this.element.classList.toggle('ol-forceopen');
    }

    public show() {
        this.element.classList.add('ol-forceopen');
    }

    public hide() {
        this.element.classList.remove('ol-forceopen');
    }
}
